import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Clock, ArrowLeft, AlertTriangle, CheckCircle, Wrench, Droplets, Sun, X, Search } from 'lucide-react';
import { brandTheme } from '../../styles/BrandTheme';

const ArticleContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${brandTheme.colors.primary[600]};
  text-decoration: none;
  margin-bottom: 2rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    color: ${brandTheme.colors.primary[700]};
    gap: 0.75rem;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(-4px);
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 3rem;

  .category {
    display: inline-block;
    background: ${brandTheme.colors.primary[100]};
    color: ${brandTheme.colors.primary[700]};
    padding: 0.5rem 1rem;
    border-radius: ${brandTheme.borderRadius.full};
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  h1 {
    font-size: clamp(2rem, 4vw, 3rem);
    line-height: 1.2;
    margin-bottom: 1.5rem;
    color: ${brandTheme.colors.secondary[900]};
  }

  .excerpt {
    font-size: 1.25rem;
    color: ${brandTheme.colors.secondary[600]};
    line-height: 1.6;
    margin-bottom: 2rem;
  }

  .meta {
    display: flex;
    gap: 2rem;
    align-items: center;
    color: ${brandTheme.colors.neutral.gray[500]};
    font-size: 0.95rem;
    flex-wrap: wrap;

    svg {
      width: 18px;
      height: 18px;
      margin-right: 0.5rem;
    }

    @media (max-width: 768px) {
      gap: 1rem;
    }
  }
`;

const ArticleImage = styled.div`
  height: 400px;
  background: linear-gradient(135deg, ${brandTheme.colors.primary[100]}, ${brandTheme.colors.primary[200]});
  border-radius: ${brandTheme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(59,130,246,0.1)"/></svg>');
  }

  .placeholder {
    font-size: 4rem;
    color: ${brandTheme.colors.primary[300]};
  }
`;

const ArticleContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${brandTheme.colors.secondary[700]};

  h2 {
    font-size: 1.75rem;
    margin: 3rem 0 1.5rem 0;
    color: ${brandTheme.colors.secondary[900]};
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: -1.5rem;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 24px;
      background: linear-gradient(180deg, ${brandTheme.colors.primary[500]}, ${brandTheme.colors.primary[700]});
      border-radius: 2px;

      @media (max-width: 768px) {
        left: -1rem;
      }
    }

    @media (max-width: 768px) {
      font-size: 1.5rem;
      margin-left: 1rem;
    }
  }

  h3 {
    font-size: 1.4rem;
    margin: 2.5rem 0 1rem 0;
    color: ${brandTheme.colors.secondary[900]};
  }

  p {
    margin-bottom: 1.5rem;
  }

  ul, ol {
    margin: 1.5rem 0;
    padding-left: 2rem;

    li {
      margin-bottom: 0.75rem;
    }
  }
`;

const ProblemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProblemCard = styled.div`
  background: white;
  border-radius: ${brandTheme.borderRadius.xl};
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }

  .problem-header {
    padding: 1.5rem;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[50]}, ${brandTheme.colors.primary[100]});
    display: flex;
    align-items: center;
    gap: 1rem;

    svg {
      color: ${brandTheme.colors.primary[600]};
      flex-shrink: 0;
    }

    h4 {
      margin: 0;
      color: ${brandTheme.colors.secondary[900]};
      font-size: 1.1rem;
    }
  }

  .problem-content {
    padding: 1.5rem;

    .severity {
      display: inline-block;
      padding: 0.25rem 0.75rem;
      border-radius: ${brandTheme.borderRadius.full};
      font-size: 0.8rem;
      font-weight: 600;
      margin-bottom: 1rem;

      &.low {
        background: #f0fdf4;
        color: #166534;
      }

      &.medium {
        background: #fef3c7;
        color: #92400e;
      }

      &.high {
        background: #fef2f2;
        color: #991b1b;
      }
    }

    p {
      margin: 0 0 1rem 0;
      color: ${brandTheme.colors.secondary[600]};
      line-height: 1.6;
    }

    .causes {
      background: ${brandTheme.colors.neutral.gray[50]};
      padding: 1rem;
      border-radius: ${brandTheme.borderRadius.lg};
      margin-bottom: 1rem;

      h5 {
        margin: 0 0 0.5rem 0;
        color: ${brandTheme.colors.secondary[900]};
        font-size: 0.9rem;
      }

      ul {
        margin: 0;
        padding-left: 1.5rem;

        li {
          margin-bottom: 0.25rem;
          font-size: 0.85rem;
          color: ${brandTheme.colors.secondary[600]};
        }
      }
    }

    .solutions {
      h5 {
        margin: 0 0 0.5rem 0;
        color: ${brandTheme.colors.secondary[900]};
        font-size: 0.9rem;
      }

      ul {
        margin: 0;
        padding-left: 1.5rem;

        li {
          margin-bottom: 0.25rem;
          font-size: 0.85rem;
          color: ${brandTheme.colors.secondary[600]};
        }
      }
    }
  }
`;

const InfoBox = styled.div<{ type?: 'warning' | 'tip' | 'success' | 'danger' }>`
  background: ${props => {
    switch(props.type) {
      case 'warning': return '#fef3c7';
      case 'tip': return '#dbeafe';
      case 'success': return '#f0fdf4';
      case 'danger': return '#fef2f2';
      default: return '#dbeafe';
    }
  }};
  border: 1px solid ${props => {
    switch(props.type) {
      case 'warning': return '#fbbf24';
      case 'tip': return '#FECE1B';
      case 'success': return '#22c55e';
      case 'danger': return '#ef4444';
      default: return '#FECE1B';
    }
  }};
  border-radius: ${brandTheme.borderRadius.lg};
  padding: 1.5rem;
  margin: 2rem 0;
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  svg {
    color: ${props => {
      switch(props.type) {
        case 'warning': return '#f59e0b';
        case 'tip': return '#FECE1B';
        case 'success': return '#16a34a';
        case 'danger': return '#dc2626';
        default: return '#FECE1B';
      }
    }};
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  div {
    h4 {
      margin: 0 0 0.5rem 0;
      color: ${props => {
        switch(props.type) {
          case 'warning': return '#92400e';
          case 'tip': return '#d97706';
          case 'success': return '#14532d';
          case 'danger': return '#991b1b';
          default: return '#d97706';
        }
      }};
      font-size: 1rem;
      font-weight: 600;
    }

    p {
      margin: 0;
      color: ${props => {
        switch(props.type) {
          case 'warning': return '#92400e';
          case 'tip': return '#d97706';
          case 'success': return '#14532d';
          case 'danger': return '#991b1b';
          default: return '#d97706';
        }
      }};
    }
  }
`;

const QuickFixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 2rem 0;
  background: white;
  border-radius: ${brandTheme.borderRadius.lg};
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid ${brandTheme.colors.neutral.gray[200]};
  }

  th {
    background: ${brandTheme.colors.primary[50]};
    font-weight: 600;
    color: ${brandTheme.colors.secondary[900]};
  }

  tr:last-child td {
    border-bottom: none;
  }

  .quick-fix {
    background: #f0fdf4;
    color: #166534;
    font-weight: 600;
  }

  .professional-needed {
    background: #fef2f2;
    color: #991b1b;
    font-weight: 600;
  }
`;

const PreventionGuide = styled.div`
  background: ${brandTheme.colors.primary[50]};
  border-radius: ${brandTheme.borderRadius.lg};
  padding: 2rem;
  margin: 2rem 0;

  h3 {
    color: ${brandTheme.colors.primary[700]};
    margin-bottom: 1.5rem;
  }

  .prevention-category {
    margin-bottom: 2rem;

    h4 {
      margin-bottom: 1rem;
      color: ${brandTheme.colors.secondary[900]};
      font-size: 1.1rem;
    }

    ul {
      margin: 0;
      padding-left: 1.5rem;

      li {
        margin-bottom: 0.5rem;
        color: ${brandTheme.colors.secondary[700]};
      }
    }
  }
`;

const EmergencyGuide = styled.div`
  background: white;
  border-radius: ${brandTheme.borderRadius.xl};
  padding: 2rem;
  margin: 2rem 0;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);

  h3 {
    margin-bottom: 1.5rem;
    color: ${brandTheme.colors.secondary[900]};
  }

  .emergency-item {
    background: ${brandTheme.colors.neutral.gray[50]};
    padding: 1.5rem;
    border-radius: ${brandTheme.borderRadius.lg};
    margin-bottom: 1rem;

    h4 {
      margin: 0 0 0.75rem 0;
      color: ${brandTheme.colors.secondary[900]};
      font-size: 1rem;
    }

    p {
      margin: 0;
      color: ${brandTheme.colors.secondary[600]};
      font-size: 0.95rem;
      line-height: 1.5;
    }
  }
`;

const CTA = styled.div`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]}, ${brandTheme.colors.primary[800]});
  border-radius: ${brandTheme.borderRadius.xl};
  padding: 2rem;
  text-align: center;
  color: white;
  margin: 3rem 0;

  h3 {
    color: white;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1.5rem;
    opacity: 0.9;
  }

  .cta-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;

    a {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 2rem;
      border-radius: ${brandTheme.borderRadius.lg};
      text-decoration: none;
      font-weight: 600;
      transition: all 0.3s ease;

      &.primary {
        background: white;
        color: ${brandTheme.colors.primary[600]};

        &:hover {
          background: ${brandTheme.colors.primary[50]};
          transform: translateY(-2px);
        }
      }

      &.secondary {
        background: rgba(255, 255, 255, 0.2);
        color: white;
        border: 2px solid rgba(255, 255, 255, 0.3);

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }
`;

const ResinBoundTroubleshootingGuidePage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Resin Bound Surface Troubleshooting Guide: Common Issues and Solutions | ZAN RESIN SURFACES LTD</title>
        <meta name="description" content="Complete troubleshooting guide for resin bound surface problems. Identify issues, understand causes, and find solutions for common problems with expert advice." />
        <meta name="keywords" content="resin bound troubleshooting, resin surface problems, resin driveway issues, surface repair guide, resin bound maintenance, London resin specialists" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/blog/resin-bound-troubleshooting-guide/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Resin Bound Surface Troubleshooting Guide: Common Issues and Solutions" />
        <meta property="og:description" content="Complete troubleshooting guide for resin bound surface problems. Identify issues, understand causes, and find solutions for common problems." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/blog/resin-bound-troubleshooting-guide/" />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-bound-troubleshooting-guide-og.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Resin bound surface troubleshooting guide" />
        <meta property="og:site_name" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:published_time" content="2024-12-20T00:00:00+00:00" />
        <meta property="article:modified_time" content="2024-12-20T00:00:00+00:00" />
        <meta property="article:author" content="ZAN RESIN SURFACES LTD" />
        <meta property="article:section" content="Troubleshooting Guide" />
        <meta property="article:tag" content="resin bound troubleshooting" />
        <meta property="article:tag" content="resin surface problems" />
        <meta property="article:tag" content="resin driveway issues" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Resin Bound Surface Troubleshooting Guide: Common Issues and Solutions" />
        <meta name="twitter:description" content="Complete troubleshooting guide for resin bound surface problems. Identify issues, understand causes, and find solutions for common problems." />
        <meta name="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-bound-troubleshooting-guide-og.jpg" />
        <meta name="twitter:image:alt" content="Resin bound surface troubleshooting guide" />
        <meta name="twitter:site" content="@resinsurfaceslondon" />
        <meta name="twitter:creator" content="@resinsurfaceslondon" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="GB-LND" />
        <meta name="geo.placename" content="London" />
        <meta name="geo.position" content="51.5074;-0.1278" />
        <meta name="ICBM" content="51.5074, -0.1278" />
        <meta name="language" content="en-GB" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="referrer" content="no-referrer-when-downgrade" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "Resin Bound Surface Troubleshooting Guide: Common Issues and Solutions",
            "description": "Complete troubleshooting guide for resin bound surface problems. Identify issues, understand causes, and find solutions for common problems with expert advice.",
            "url": "https://www.resinsurfaceslondon.co.uk/blog/resin-bound-troubleshooting-guide/",
            "datePublished": "2024-12-20T00:00:00+00:00",
            "dateModified": "2024-12-20T00:00:00+00:00",
            "author": {
              "@type": "Organization",
              "name": "ZAN RESIN SURFACES LTD",
              "url": "https://www.resinsurfaceslondon.co.uk",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg"
              },
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "London",
                "addressCountry": "GB"
              }
            },
            "publisher": {
              "@type": "Organization",
              "name": "ZAN RESIN SURFACES LTD",
              "url": "https://www.resinsurfaceslondon.co.uk",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.resinsurfaceslondon.co.uk/blog/resin-bound-troubleshooting-guide/"
            },
            "image": {
              "@type": "ImageObject",
              "url": "https://www.resinsurfaceslondon.co.uk/images/resin-bound-troubleshooting-guide-og.jpg",
              "width": 1200,
              "height": 630
            },
            "wordCount": 2800,
            "timeRequired": "PT9M",
            "articleSection": "Troubleshooting Guide",
            "keywords": "resin bound troubleshooting, resin surface problems, resin driveway issues, surface repair guide, resin bound maintenance, London resin specialists",
            "inLanguage": "en-GB",
            "isAccessibleForFree": true,
            "speakable": {
              "@type": "SpeakableSpecification",
              "cssSelector": "h1, h2, h3, .excerpt"
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Resin Bound Troubleshooting"
              },
              {
                "@type": "Thing", 
                "name": "Surface Repair"
              },
              {
                "@type": "Thing",
                "name": "Resin Bound Maintenance"
              }
            ]
          })}
        </script>
      </Helmet>

      <ArticleContainer>
        <BackLink to="/blog/">
          <ArrowLeft size={18} />
          Back to Blog
        </BackLink>

        <ArticleHeader>
          <span className="category">Troubleshooting</span>
          <h1>Resin Bound Surface Troubleshooting Guide: Common Issues and Solutions</h1>
          <p className="excerpt">
            Complete troubleshooting guide for resin bound surface problems. Identify issues, understand causes, and find solutions for common problems with expert advice.
          </p>
          <div className="meta">
            <span>
              <Calendar size={18} />
              December 20, 2024
            </span>
            <span>
              <Clock size={18} />
              9 min read
            </span>
            <span>
              <Wrench size={18} />
              Problem Solving
            </span>
          </div>
        </ArticleHeader>

        <ArticleImage>
          <img 
            src="/images/resin-surfaces-bound-work6.jpeg" 
            alt="Resin bound troubleshooting guide showing maintenance and repair solutions"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 'inherit'
            }}
          />
        </ArticleImage>

        <ArticleContent>
          <p>
            Even with proper installation and maintenance, resin bound surfaces can occasionally develop issues. Understanding common problems, their causes, and solutions helps you address issues quickly and effectively, preventing minor problems from becoming major repairs.
          </p>

          <p>
            This comprehensive troubleshooting guide covers the most common resin bound surface problems, their underlying causes, and step-by-step solutions to restore your surface to perfect condition.
          </p>

          <h2>Common Surface Problems</h2>

          <ProblemGrid>
            <ProblemCard>
              <div className="problem-header">
                <AlertTriangle size={20} />
                <h4>Surface Cracking</h4>
              </div>
              <div className="problem-content">
                <span className="severity high">High Priority</span>
                <p>Visible cracks appearing in the resin bound surface, ranging from hairline to significant gaps.</p>
                <div className="causes">
                  <h5>Common Causes:</h5>
                  <ul>
                    <li>Insufficient sub-base preparation</li>
                    <li>Poor drainage causing water damage</li>
                    <li>Extreme temperature variations</li>
                    <li>Heavy loads exceeding design capacity</li>
                    <li>Inadequate expansion joints</li>
                  </ul>
                </div>
                <div className="solutions">
                  <h5>Solutions:</h5>
                  <ul>
                    <li>Assess crack severity and extent</li>
                    <li>Clean cracks thoroughly</li>
                    <li>Fill minor cracks with resin repair compound</li>
                    <li>Professional repair for major cracks</li>
                    <li>Address underlying drainage issues</li>
                  </ul>
                </div>
              </div>
            </ProblemCard>

            <ProblemCard>
              <div className="problem-header">
                <Droplets size={20} />
                <h4>Poor Drainage</h4>
              </div>
              <div className="problem-content">
                <span className="severity medium">Medium Priority</span>
                <p>Water pooling on the surface or slow drainage through the permeable surface.</p>
                <div className="causes">
                  <h5>Common Causes:</h5>
                  <ul>
                    <li>Insufficient drainage fall</li>
                    <li>Blocked drainage channels</li>
                    <li>Compacted sub-base</li>
                    <li>Debris blocking permeability</li>
                    <li>Improper surface leveling</li>
                  </ul>
                </div>
                <div className="solutions">
                  <h5>Solutions:</h5>
                  <ul>
                    <li>Clear all drainage channels</li>
                    <li>Remove debris from surface</li>
                    <li>Check surface levels and falls</li>
                    <li>Power wash to restore permeability</li>
                    <li>Professional drainage assessment</li>
                  </ul>
                </div>
              </div>
            </ProblemCard>

            <ProblemCard>
              <div className="problem-header">
                <Sun size={20} />
                <h4>Color Fading</h4>
              </div>
              <div className="problem-content">
                <span className="severity low">Low Priority</span>
                <p>Gradual loss of color intensity or changes in surface appearance over time.</p>
                <div className="causes">
                  <h5>Common Causes:</h5>
                  <ul>
                    <li>Prolonged UV exposure</li>
                    <li>Use of harsh cleaning chemicals</li>
                    <li>Poor quality materials</li>
                    <li>Inadequate surface protection</li>
                    <li>Natural weathering process</li>
                  </ul>
                </div>
                <div className="solutions">
                  <h5>Solutions:</h5>
                  <ul>
                    <li>Use UV-resistant cleaning products</li>
                    <li>Avoid harsh chemicals</li>
                    <li>Apply protective sealant</li>
                    <li>Regular gentle cleaning</li>
                    <li>Consider professional restoration</li>
                  </ul>
                </div>
              </div>
            </ProblemCard>

            <ProblemCard>
              <div className="problem-header">
                <X size={20} />
                <h4>Surface Lifting</h4>
              </div>
              <div className="problem-content">
                <span className="severity high">High Priority</span>
                <p>Areas of the surface lifting or separating from the sub-base.</p>
                <div className="causes">
                  <h5>Common Causes:</h5>
                  <ul>
                    <li>Poor adhesion during installation</li>
                    <li>Water damage to sub-base</li>
                    <li>Inadequate surface preparation</li>
                    <li>Extreme temperature stress</li>
                    <li>Heavy impact damage</li>
                  </ul>
                </div>
                <div className="solutions">
                  <h5>Solutions:</h5>
                  <ul>
                    <li>Stop using affected areas immediately</li>
                    <li>Assess extent of lifting</li>
                    <li>Professional repair required</li>
                    <li>Address underlying moisture issues</li>
                    <li>Consider partial replacement</li>
                  </ul>
                </div>
              </div>
            </ProblemCard>

            <ProblemCard>
              <div className="problem-header">
                <Search size={20} />
                <h4>Surface Staining</h4>
              </div>
              <div className="problem-content">
                <span className="severity medium">Medium Priority</span>
                <p>Persistent stains that don't respond to normal cleaning methods.</p>
                <div className="causes">
                  <h5>Common Causes:</h5>
                  <ul>
                    <li>Oil and grease spills</li>
                    <li>Organic matter (leaves, berries)</li>
                    <li>Chemical spills</li>
                    <li>Rust from metal objects</li>
                    <li>Ink or paint contamination</li>
                  </ul>
                </div>
                <div className="solutions">
                  <h5>Solutions:</h5>
                  <ul>
                    <li>Identify stain type and source</li>
                    <li>Use appropriate cleaning products</li>
                    <li>Apply targeted stain removers</li>
                    <li>Power wash with hot water</li>
                    <li>Professional cleaning if needed</li>
                  </ul>
                </div>
              </div>
            </ProblemCard>

            <ProblemCard>
              <div className="problem-header">
                <AlertTriangle size={20} />
                <h4>Edge Damage</h4>
              </div>
              <div className="problem-content">
                <span className="severity medium">Medium Priority</span>
                <p>Damage to edges, including chipping, crumbling, or separation from restraints.</p>
                <div className="causes">
                  <h5>Common Causes:</h5>
                  <ul>
                    <li>Heavy machinery contact</li>
                    <li>Frost damage</li>
                    <li>Inadequate edge restraints</li>
                    <li>Poor edge finishing</li>
                    <li>Vehicle wheel damage</li>
                  </ul>
                </div>
                <div className="solutions">
                  <h5>Solutions:</h5>
                  <ul>
                    <li>Clean damaged areas</li>
                    <li>Remove loose material</li>
                    <li>Apply edge repair compound</li>
                    <li>Check and repair restraints</li>
                    <li>Professional edge restoration</li>
                  </ul>
                </div>
              </div>
            </ProblemCard>
          </ProblemGrid>

          <InfoBox type="tip">
            <CheckCircle size={20} />
            <div>
              <h4>Early Detection Tip</h4>
              <p>Regular inspection helps identify problems early when they're easier and less expensive to repair. Check your surface monthly for signs of issues.</p>
            </div>
          </InfoBox>

          <h2>Quick Fix Reference Table</h2>

          <QuickFixTable>
            <thead>
              <tr>
                <th>Problem</th>
                <th>Quick Fix</th>
                <th>Time Required</th>
                <th>Professional Needed?</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Minor Stains</strong></td>
                <td>Clean with appropriate detergent and scrub brush</td>
                <td>15-30 minutes</td>
                <td className="quick-fix">No</td>
              </tr>
              <tr>
                <td><strong>Hairline Cracks</strong></td>
                <td>Clean and fill with resin repair compound</td>
                <td>1-2 hours</td>
                <td className="quick-fix">No</td>
              </tr>
              <tr>
                <td><strong>Poor Drainage</strong></td>
                <td>Clear drainage channels and debris</td>
                <td>30-60 minutes</td>
                <td className="quick-fix">No</td>
              </tr>
              <tr>
                <td><strong>Minor Edge Damage</strong></td>
                <td>Clean area and apply edge repair compound</td>
                <td>1-2 hours</td>
                <td className="quick-fix">No</td>
              </tr>
              <tr>
                <td><strong>Major Cracking</strong></td>
                <td>Professional assessment and repair required</td>
                <td>1-3 days</td>
                <td className="professional-needed">Yes</td>
              </tr>
              <tr>
                <td><strong>Surface Lifting</strong></td>
                <td>Professional repair or replacement required</td>
                <td>1-5 days</td>
                <td className="professional-needed">Yes</td>
              </tr>
              <tr>
                <td><strong>Structural Damage</strong></td>
                <td>Professional assessment and major repair</td>
                <td>3-7 days</td>
                <td className="professional-needed">Yes</td>
              </tr>
            </tbody>
          </QuickFixTable>

          <h2>Step-by-Step Repair Procedures</h2>

          <h3>Minor Crack Repair</h3>
          <ol>
            <li><strong>Clean the Crack:</strong> Remove debris and loose material using a stiff brush</li>
            <li><strong>Widen if Necessary:</strong> Use a chisel to create a V-shaped groove for better adhesion</li>
            <li><strong>Apply Primer:</strong> Use appropriate primer to improve adhesion</li>
            <li><strong>Fill the Crack:</strong> Apply resin repair compound using a putty knife</li>
            <li><strong>Smooth the Surface:</strong> Level the repair compound with the surrounding surface</li>
            <li><strong>Allow to Cure:</strong> Follow manufacturer's curing instructions</li>
          </ol>

          <h3>Stain Removal Process</h3>
          <ol>
            <li><strong>Identify Stain Type:</strong> Determine the source and type of stain</li>
            <li><strong>Blot Fresh Stains:</strong> Immediately blot liquid spills with absorbent material</li>
            <li><strong>Apply Appropriate Cleaner:</strong> Use suitable cleaning product for stain type</li>
            <li><strong>Scrub Gently:</strong> Use soft brush to work cleaner into stain</li>
            <li><strong>Rinse Thoroughly:</strong> Remove all cleaning residue with clean water</li>
            <li><strong>Repeat if Necessary:</strong> Repeat process for stubborn stains</li>
          </ol>

          <h3>Drainage Restoration</h3>
          <ol>
            <li><strong>Clear Surface Debris:</strong> Remove all loose material from surface</li>
            <li><strong>Check Drainage Channels:</strong> Ensure all channels are clear</li>
            <li><strong>Power Wash:</strong> Use pressure washer to clean surface and restore permeability</li>
            <li><strong>Check Falls:</strong> Verify proper drainage falls are maintained</li>
            <li><strong>Test Drainage:</strong> Pour water and observe drainage patterns</li>
            <li><strong>Address Issues:</strong> Fix any remaining drainage problems</li>
          </ol>

          <InfoBox type="warning">
            <AlertTriangle size={20} />
            <div>
              <h4>Safety Warning</h4>
              <p>Always wear appropriate safety equipment when performing repairs. Avoid contact with chemicals and ensure proper ventilation during work.</p>
            </div>
          </InfoBox>

          <h2>Prevention Strategies</h2>

          <PreventionGuide>
            <h3>Regular Maintenance Prevention</h3>
            <div className="prevention-category">
              <h4>Weekly Maintenance</h4>
              <ul>
                <li>Remove debris and loose materials</li>
                <li>Check for new stains or damage</li>
                <li>Clear drainage channels</li>
                <li>Inspect edges and restraints</li>
              </ul>
            </div>
            <div className="prevention-category">
              <h4>Monthly Maintenance</h4>
              <ul>
                <li>Thorough surface cleaning</li>
                <li>Power wash to maintain permeability</li>
                <li>Check for signs of wear or damage</li>
                <li>Address minor issues promptly</li>
              </ul>
            </div>
            <div className="prevention-category">
              <h4>Seasonal Maintenance</h4>
              <ul>
                <li>Spring: Deep clean and inspect for winter damage</li>
                <li>Summer: Monitor for UV damage and heat stress</li>
                <li>Autumn: Remove organic matter and prepare for winter</li>
                <li>Winter: Protect from frost and manage snow/ice</li>
              </ul>
            </div>
          </PreventionGuide>

          <h2>When to Call Professionals</h2>

          <EmergencyGuide>
            <h3>Professional Repair Required For:</h3>
            <div className="emergency-item">
              <h4>Major Structural Issues</h4>
              <p>Large cracks, significant lifting, or structural damage require professional assessment and repair to ensure safety and proper restoration.</p>
            </div>
            <div className="emergency-item">
              <h4>Widespread Problems</h4>
              <p>Issues affecting large areas of the surface often indicate underlying problems that need professional diagnosis and treatment.</p>
            </div>
            <div className="emergency-item">
              <h4>Warranty Coverage</h4>
              <p>Problems covered under warranty should be addressed by authorized professionals to maintain warranty validity.</p>
            </div>
            <div className="emergency-item">
              <h4>Safety Concerns</h4>
              <p>Any issues that create safety hazards or affect structural integrity require immediate professional attention.</p>
            </div>
          </EmergencyGuide>

          <h2>Emergency Response Procedures</h2>

          <h3>Immediate Actions for Serious Problems</h3>
          <ul>
            <li><strong>Stop Use:</strong> Immediately stop using affected areas to prevent further damage</li>
            <li><strong>Document Issues:</strong> Take photos and notes of the problem for professional assessment</li>
            <li><strong>Contact Professionals:</strong> Reach out to qualified repair specialists promptly</li>
            <li><strong>Protect Area:</strong> Prevent access to damaged areas until repairs are completed</li>
            <li><strong>Check Insurance:</strong> Review insurance coverage for potential claims</li>
          </ul>

          <h3>Information to Provide to Professionals</h3>
          <ul>
            <li>Detailed description of the problem</li>
            <li>Photos of the damage from multiple angles</li>
            <li>When the problem was first noticed</li>
            <li>Any recent changes or events</li>
            <li>Previous maintenance history</li>
            <li>Installation details and warranty information</li>
          </ul>

          <CTA>
            <h3>Need Professional Repair Services?</h3>
            <p>
              Our expert team can diagnose and repair any resin bound surface issues quickly and effectively. Contact us for professional assessment and repair services.
            </p>
            <div className="cta-buttons">
              <Link to="/contact/" className="primary">
                <Wrench size={18} />
                Get Repair Quote
              </Link>
              <Link to="/services/" className="secondary">
                <CheckCircle size={18} />
                View Repair Services
              </Link>
            </div>
          </CTA>

          <h2>Conclusion</h2>

          <p>
            Most resin bound surface problems can be prevented with regular maintenance and addressed quickly when caught early. Understanding common issues and their solutions helps you maintain your surface in excellent condition and avoid costly repairs.
          </p>

          <p>
            While many minor issues can be resolved with DIY approaches, don't hesitate to contact professionals for major problems or when in doubt. Professional repair ensures proper restoration and maintains the long-term value of your investment.
          </p>

          <p>
            Regular inspection, prompt attention to issues, and appropriate maintenance will keep your resin bound surface looking and performing perfectly for years to come. Remember, prevention is always more cost-effective than repair.
          </p>
        </ArticleContent>
      </ArticleContainer>
    </>
  );
};

export default ResinBoundTroubleshootingGuidePage;
